import { useState } from 'react'

import { useSupportStore } from '../store'
import { type UnreadEntryWindow, withLoadedMessages } from '../threadTimeline'
import type { SupportMessage, SupportTicket } from '../types'

interface CapturedWindow extends UnreadEntryWindow {
  ticketPublicId: string | null
  boundToLoadedThread: boolean
}

const windowFor = (ticket: SupportTicket | null): CapturedWindow => ({
  ticketPublicId: ticket?.publicId ?? null,
  boundToLoadedThread: false,
  since: ticket
    ? (useSupportStore.getState().lastSeenAt[ticket.publicId] ?? null)
    : null,
  until: ticket?.lastMessageAt ?? null,
})

// What counted as unread the moment the thread opened. Captured during render
// because opening the thread stamps the watermark forward in an effect, and the
// entry marker has to remember what it was before that. The upper bound is
// widened once, by the first load, so replies the list row had never heard of
// still get the marker; anything arriving after that is being watched live.
export const useUnreadEntryWindow = (
  ticket: SupportTicket | null,
  messages: SupportMessage[],
): UnreadEntryWindow => {
  const [captured, setCaptured] = useState(() => windowFor(ticket))

  if (captured.ticketPublicId !== (ticket?.publicId ?? null)) {
    const opened = windowFor(ticket)
    setCaptured(opened)
    return opened
  }

  if (!captured.boundToLoadedThread && messages.length > 0) {
    const loaded = {
      ...captured,
      until: withLoadedMessages(captured, messages).until,
      boundToLoadedThread: true,
    }
    setCaptured(loaded)
    return loaded
  }

  return captured
}
